import BottomSheet, { BottomSheetBackdrop, BottomSheetView } from '@gorhom/bottom-sheet';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import QRCode from 'react-native-qrcode-svg';

import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';

import { Heading } from './Heading';
import { Text } from './Text';

export interface QrSheetProps {
  /** The URL the QR code encodes — already includes the tracking query param. */
  url: string | null;
  /** Optional caption shown under the QR — usually the link's label/group. */
  caption?: string | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Bottom sheet that renders a tracked referral link as a scannable QR
 * code. Reuses the existing `@gorhom/bottom-sheet` patterns (same
 * backdrop, snapPoint behavior) used by `ShareMenu`. The component is
 * stateless — caller decides which URL is shown.
 *
 * On web export, `react-native-qrcode-svg` renders SVG identically.
 * Verified at the bundle level by `expo export --platform web`.
 */
export function QrSheet({ url, caption, isOpen, onClose }: QrSheetProps) {
  const sheetRef = useRef<BottomSheet>(null);
  const snapPoints = useMemo<(string | number)[]>(() => ['60%'], []);

  // Track the URL the sheet was opened with so the QR doesn't re-render
  // a fresh code if the parent re-renders for unrelated reasons.
  const [pinnedUrl, setPinnedUrl] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen) setPinnedUrl(url);
  }, [isOpen, url]);

  const renderBackdrop = (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
    <BottomSheetBackdrop {...props} appearsOnIndex={0} disappearsOnIndex={-1} />
  );

  const handleSheetChange = (index: number) => {
    if (index === -1) onClose();
  };

  return (
    <BottomSheet
      ref={sheetRef}
      index={isOpen ? 0 : -1}
      snapPoints={snapPoints}
      enablePanDownToClose
      backdropComponent={renderBackdrop}
      onChange={handleSheetChange}
      handleIndicatorStyle={styles.handle}
      backgroundStyle={styles.background}
    >
      <BottomSheetView style={styles.content}>
        <Heading size="2xl" style={styles.heading}>
          Scan to share
        </Heading>
        <View style={styles.qrFrame}>
          {pinnedUrl ? (
            <QRCode
              value={pinnedUrl}
              // High error correction so the code stays scannable even
              // when shown briefly on a phone screen at moderate sizes.
              ecl="H"
              size={Platform.OS === 'web' ? 240 : 220}
              backgroundColor={colors.frost}
              color={colors.deepGreen}
            />
          ) : null}
        </View>
        {caption ? (
          <Text variant="caption" color="textMuted" style={styles.caption}>
            {caption}
          </Text>
        ) : null}
        <Text variant="caption" color="textMuted" style={styles.caption}>
          Have your friend point their camera at this code.
        </Text>
      </BottomSheetView>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  handle: { backgroundColor: colors.border },
  background: { backgroundColor: colors.surface, borderRadius: radii.lg },
  content: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.base,
    paddingBottom: spacing.xl,
    gap: spacing.lg,
    alignItems: 'center',
  },
  heading: { alignSelf: 'flex-start' },
  qrFrame: {
    padding: spacing.lg,
    backgroundColor: colors.frost,
    borderRadius: radii.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  caption: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    textAlign: 'center',
  },
});
